import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconDownload, IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { downloadFile } from '../../utils/download';

interface PackRow {
  id: number;
  name: string;
  description: string | null;
  report_keys: string[];
  created_by: { full_name: string } | null;
}

interface PackForm {
  name: string;
  description: string;
  report_keys: string[];
}

interface CatalogEntry {
  key: string;
  label: string;
}

type Catalog = Record<string, CatalogEntry[]>;

const emptyForm: PackForm = { name: '', description: '', report_keys: [] };

export function ReportPacks() {
  const confirmDelete = useConfirm();
  const { showError, showSuccess } = useToast();

  const [packs, setPacks] = useState<PackRow[] | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [editing, setEditing] = useState<'new' | PackRow | null>(null);
  const [form, setForm] = useState<PackForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const [generating, setGenerating] = useState<PackRow | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = () => api.get<PackRow[]>('/report-packs').then((res) => setPacks(res.data));

  useEffect(() => {
    load();
    api.get<Catalog>('/report-packs/catalog').then((res) => setCatalog(res.data));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (pack: PackRow) => {
    setForm({ name: pack.name, description: pack.description ?? '', report_keys: pack.report_keys });
    setErrors({});
    setEditing(pack);
  };

  const set = (key: 'name' | 'description') => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const toggleKey = (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({
      ...f,
      report_keys: e.target.checked ? [...f.report_keys, key] : f.report_keys.filter((k) => k !== key),
    }));
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/report-packs', form);
      else if (editing) await api.put(`/report-packs/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (pack: PackRow) => {
    if (!(await confirmDelete(`Delete report pack "${pack.name}"?`))) return;
    try {
      await api.delete(`/report-packs/${pack.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const openGenerate = (pack: PackRow) => {
    setDateFrom('');
    setDateTo('');
    setGenerating(pack);
  };

  const submitGenerate = async (e: FormEvent) => {
    e.preventDefault();
    if (!generating) return;
    setDownloading(true);
    try {
      await downloadFile(
        api,
        `/report-packs/${generating.id}/generate`,
        `${generating.name.toLowerCase().replace(/\s+/g, '-')}.pdf`,
        { date_from: dateFrom || undefined, date_to: dateTo || undefined }
      );
      setGenerating(null);
      showSuccess('Pack generated.');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Generation failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Report Packs</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + New Pack
        </button>
      </header>
      <p className="muted">
        Combine several existing reports into one downloadable PDF — a shared date range applies to every included
        report that uses one; anything else uses its own default. Only reports you have permission to view can be
        added, and generating a pack always reflects your own current access, even for a pack someone else created.
      </p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Reports</th>
                <th>Created By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {packs?.map((pack) => (
                <tr key={pack.id}>
                  <td>{pack.name}</td>
                  <td className="muted">{pack.description || '—'}</td>
                  <td className="muted">{pack.report_keys.length}</td>
                  <td className="muted">{pack.created_by?.full_name ?? '—'}</td>
                  <td className="right">
                    <IconButton icon={IconDownload} label="Generate" onClick={() => openGenerate(pack)} />
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(pack)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(pack)} />
                  </td>
                </tr>
              ))}
              {packs?.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No report packs yet — click "New Pack" to build one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'New report pack' : 'Edit report pack'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Description
              <textarea rows={2} value={form.description} onChange={set('description')} />
              {errors.description && <small className="error">{errors.description[0]}</small>}
            </label>
            <div className="span-2">
              <p className="muted" style={{ marginBottom: 8 }}>
                Reports to include *
              </p>
              {errors.report_keys && <small className="error">{errors.report_keys[0]}</small>}
              {catalog &&
                Object.entries(catalog).map(([category, entries]) => (
                  <fieldset key={category} style={{ marginBottom: 12 }}>
                    <legend>{category}</legend>
                    {entries.map((entry) => (
                      <label key={entry.key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={form.report_keys.includes(entry.key)}
                          onChange={toggleKey(entry.key)}
                        />
                        {entry.label}
                      </label>
                    ))}
                  </fieldset>
                ))}
            </div>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {generating && (
        <Modal title={`Generate "${generating.name}"`} onClose={() => setGenerating(null)}>
          <form onSubmit={submitGenerate} className="form-grid">
            <p className="muted span-2">
              Leave blank to use the current month. This range applies to every included report that uses a date
              filter; reports with other filters (status, category) use their own default.
            </p>
            <label>
              From
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setGenerating(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={downloading}>
                {downloading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
